import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DeviceService } from "@core/services/device.service";
import { UtilsService } from "@core/services/utils/utils.service";

@Component({
  selector: "astrobin-otp-input",
  templateUrl: "./otp-input.component.html",
  styleUrls: ["./otp-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpInputComponent),
      multi: true
    }
  ]
})
export class OtpInputComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() length = 6;
  @Input() disabled = false;
  @Input() autoFocus = true;
  @Input() inputType: "text" | "number" = "number";
  @Input() placeholder = "·";

  @Output() otpComplete = new EventEmitter<string>();
  @Output() otpChange = new EventEmitter<string>();

  @ViewChildren("otpInput") otpInputs: QueryList<ElementRef>;

  values: string[] = [];
  private _onChange: (value: string) => void;
  private _onTouched: () => void;

  constructor(
    private readonly utilsService: UtilsService,
    private readonly deviceService: DeviceService
  ) {}

  ngOnInit(): void {
    this.values = new Array(this.length).fill("");
  }

  ngAfterViewInit(): void {
    // Auto-focus the first input if autoFocus is enabled and not on touch device
    if (this.autoFocus && !this.deviceService.isTouchEnabled() && this.otpInputs && this.otpInputs.length > 0) {
      this.utilsService.delay(100).subscribe(() => {
        this.focusInput(0);
      });
    }
  }

  writeValue(value: string): void {
    if (value) {
      const chars = value.split("");
      this.values = chars.concat(new Array(Math.max(0, this.length - chars.length)).fill(""));
    } else {
      this.values = new Array(this.length).fill("");
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (this.inputType === "number" && !/^[0-9]*$/.test(value)) {
      value = value.replace(/[^0-9]/g, "");
      input.value = value;
    }

    if (value.length > 1) {
      const chars = value.split("");
      for (let i = 0; i < chars.length && index + i < this.length; i++) {
        this.values[index + i] = chars[i];
      }
      const nextIndex = Math.min(index + chars.length, this.length - 1);
      this.focusInput(nextIndex);
    } else {
      this.values[index] = value;
      if (value && index < this.length - 1) {
        this.focusInput(index + 1);
      }
    }

    this.updateValue();
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const key = event.key;

    if (key === "Backspace") {
      event.preventDefault();
      if (this.values[index]) {
        this.values[index] = "";
        this.updateValue();
      } else if (index > 0) {
        this.focusInput(index - 1);
        this.values[index - 1] = "";
        this.updateValue();
      }
    } else if (key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
    } else if (key === "ArrowRight" && index < this.length - 1) {
      event.preventDefault();
      this.focusInput(index + 1);
    } else if (key === "Delete") {
      event.preventDefault();
      this.values[index] = "";
      this.updateValue();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text");
    const chars = pastedData.replace(/[^0-9]/g, "").split("");

    for (let i = 0; i < chars.length && i < this.length; i++) {
      this.values[i] = chars[i];
    }

    this.updateValue();
    const lastIndex = Math.min(chars.length, this.length - 1);
    this.focusInput(lastIndex);
  }

  onFocus(): void {
    if (this._onTouched) {
      this._onTouched();
    }
  }

  focusFirst(): void {
    // Public method to focus the first input (skip on touch devices)
    if (!this.deviceService.isTouchEnabled() && this.otpInputs && this.otpInputs.length > 0) {
      this.focusInput(0);
    }
  }

  private focusInput(index: number): void {
    const inputs = this.otpInputs.toArray();
    if (inputs[index]) {
      this.utilsService.delay(0).subscribe(() => {
        inputs[index].nativeElement.focus();
        inputs[index].nativeElement.select();
      });
    }
  }

  private updateValue(): void {
    const value = this.values.join("");

    if (this._onChange) {
      this._onChange(value);
    }

    this.otpChange.emit(value);

    if (value.length === this.length && this.values.every(v => v !== "")) {
      this.otpComplete.emit(value);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
